import { FC, useContext, useRef, useState } from 'react'
import classNames from 'classnames'
import { ScrollThumb } from './ScrollThumb'
import { useElementRect } from '../../hooks/useElementRect'
import { CutterContext } from '../contexts/CutterContext'
import { useValidateVisibleRange } from '../hooks/useValidateVisibleRange'
import './Scroll.scss'
import { useDrag } from '../../hooks/useDrag'
import { InterfaceEvent } from '../../interfaces/InterfaceEvent'
import { InternalContext } from '../contexts/InternalContext'
import { useDisableUserSelect } from '../../hooks/useDisableUserSelect'
import { useDocumentCursor } from '../../hooks/useDocumentCursor'
import { PositionRange } from '../../types'
import { useAbsolutePixelSize } from '../hooks/useAbsolutePixelSize'
import { useMoveScroll } from './hooks/useMoveScroll'
import { PartialRect } from '../../interfaces/PartialRect'

const defaultRect: PartialRect = { left: 0, width: 984 }

export const Scroll: FC = () => {
  const { visibleRange, setVisibleRange } = useContext(CutterContext)
  const { setMouseCursorEnabled } = useContext(InternalContext)
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const rect = useElementRect(element) ?? defaultRect
  const validateVisibleRange = useValidateVisibleRange()
  const pixelSize = useAbsolutePixelSize(rect.width)
  const moveScroll = useMoveScroll(rect.width)
  const initialState = useRef<{ visibleRange: PositionRange; clientX: number }>()

  const drag = useDrag(element, {
    onDown({ clientX }: InterfaceEvent) {
      const position = (clientX - rect.left) * pixelSize
      const halfVisibleLength = (visibleRange[1] - visibleRange[0]) / 2
      const range = validateVisibleRange([position - halfVisibleLength, position + halfVisibleLength])
      initialState.current = { clientX, visibleRange: range }
      setVisibleRange(range)
      setMouseCursorEnabled(false)
    },
    onMove(event: InterfaceEvent) {
      if (initialState.current) {
        moveScroll(event, initialState.current)
      }
    },
    onUp() {
      setMouseCursorEnabled(true)
    },
  })

  useDisableUserSelect(drag)
  useDocumentCursor('grabbing', drag)

  return (
    <div
      className={classNames('Scroll', {
        '-grabbing': drag,
      })}
    >
      <div className="body" ref={setElement} />
      <ScrollThumb className="thumb" containerWidth={rect.width} />
    </div>
  )
}
